/* MASONTEX · Media Core — resolver.js (flat asset path)
   Pure media resolution. DOM-free.
   ctx = { config, productMedia, lists, state, hasFullConfig }
   Depends on normalize + capabilities (global MasontexMediaCore). */
(function (global) {
  'use strict';

  const api = (global.MasontexMediaCore = global.MasontexMediaCore || {});
  const resolver = {};

  resolver.colorMediaEntry = (ctx, color, structure, view) => {
    const n = api.normalize;
    const selectedColor = n.findItem(ctx.lists.colors, color);
    const mediaMap = selectedColor?.media || selectedColor?.images || selectedColor?.imageMap || {};
    return mediaMap?.[structure]?.[view]
      ?? mediaMap?.[`${structure}.${view}`]
      ?? selectedColor?.[structure]?.[view]
      ?? null;
  };

  resolver.fallbackViewImage = (ctx, view) => api.normalize.findItem(ctx.lists.views, view)?.image || '';

  resolver.compositeVisor = (ctx, color, visor, structure, view) =>
    api.normalize.extractBase(ctx.config.visorMedia?.[color]?.[visor]?.[structure]?.[view]);

  resolver.exactVisorOverlay = (ctx, color, visor, structure, view) => {
    const n = api.normalize;
    const entry = ctx.config.overlays?.[color]?.[structure]?.[view];
    if (typeof entry === 'string') return entry;
    return n.extractBase(entry?.[visor]) || n.extractOverlay(entry?.[visor]);
  };

  resolver.isClearVisor = (ctx, visor) => {
    const n = api.normalize;
    const item = n.findItem(ctx.lists.visors, visor);
    return /clear|transparent/i.test(`${visor} ${n.itemLabel(item)}`);
  };

  resolver.semanticItem = (ctx, items, pattern) => {
    const n = api.normalize;
    return items.find((item) => pattern.test(`${n.itemKey(item)} ${n.itemLabel(item)}`));
  };

  resolver.closedStructure = (ctx) => {
    const n = api.normalize;
    const found = resolver.semanticItem(ctx, ctx.lists.structures, /closed/i);
    return n.itemKey(found) || n.itemKey(ctx.lists.structures[0]);
  };

  resolver.threeQuarterView = (ctx) => {
    const n = api.normalize;
    const found = resolver.semanticItem(ctx, ctx.lists.views, /three.?quarter|3\s*\/\s*4/i);
    return n.itemKey(found) || n.itemKey(ctx.lists.views[0]);
  };

  resolver.supports = (ctx, color, visor, structure, view) => {
    const n = api.normalize;
    if (!view) return false;
    if (!ctx.hasFullConfig) return !!resolver.fallbackViewImage(ctx, view);
    const baseUrl = n.extractBase(resolver.colorMediaEntry(ctx, color, structure, view));
    if (!ctx.lists.visors.length || resolver.isClearVisor(ctx, visor)) return !!baseUrl;
    if (resolver.compositeVisor(ctx, color, visor, structure, view)) return true;
    if (resolver.exactVisorOverlay(ctx, color, visor, structure, view) && baseUrl) return true;
    return !!(api.capabilities.overlayFor(ctx, color, visor, structure, view)?.url && baseUrl);
  };

  resolver.supportedView = (ctx, structure, view) =>
    resolver.supports(ctx, ctx.state.color, ctx.state.visor, structure, view);

  resolver.supportedStructure = (ctx, structure) =>
    ctx.lists.views.some((view) => resolver.supportedView(ctx, structure, api.normalize.itemKey(view)));

  resolver.findCompatible = (ctx, visor) => {
    const n = api.normalize;
    if (!ctx.hasFullConfig) return { structure: ctx.state.structure, view: ctx.state.view };
    const closed = resolver.closedStructure(ctx);
    const threeQuarter = resolver.threeQuarterView(ctx);
    const attempts = n.unique([
      { structure: ctx.state.structure, view: ctx.state.view },
      { structure: closed, view: ctx.state.view },
      { structure: closed, view: threeQuarter },
      { structure: ctx.state.structure, view: threeQuarter },
      ...ctx.lists.structures.flatMap((structure) => ctx.lists.views.map((view) => ({
        structure: n.itemKey(structure),
        view: n.itemKey(view)
      })))
    ]);
    return attempts.find((attempt) => resolver.supports(ctx, ctx.state.color, visor, attempt.structure, attempt.view)) || null;
  };

  resolver.ensureCompatible = (ctx) => {
    const n = api.normalize;
    const working = { ...ctx.state };
    const runCtx = (state) => ({ ...ctx, state });
    if (!ctx.hasFullConfig) {
      if (!resolver.fallbackViewImage(runCtx(working), working.view)) working.view = n.itemKey(ctx.lists.views[0]);
      return working;
    }
    const findFor = (visor) => resolver.findCompatible(runCtx(working), visor);
    const compatible = findFor(working.visor);
    if (compatible) {
      working.structure = compatible.structure;
      working.view = compatible.view;
      return working;
    }
    working.visor = n.itemKey(
      ctx.lists.visors.find((visor) => resolver.isClearVisor(ctx, n.itemKey(visor))) || ctx.lists.visors[0]
    );
    const clearCompatible = findFor(working.visor);
    if (clearCompatible) {
      working.structure = clearCompatible.structure;
      working.view = clearCompatible.view;
    }
    return working;
  };

  resolver.payloadFor = (ctx, color, visor, structure, view) => {
    const n = api.normalize;
    if (!ctx.hasFullConfig) {
      const url = resolver.fallbackViewImage(ctx, view);
      return url ? { base: url, overlay: '', filter: '' } : null;
    }
    const mediaEntry = resolver.colorMediaEntry(ctx, color, structure, view);
    const baseUrl = n.extractBase(mediaEntry);
    if (!baseUrl) return null;
    if (!ctx.lists.visors.length || resolver.isClearVisor(ctx, visor)) {
      return { base: baseUrl, overlay: '', filter: '' };
    }
    const composite = resolver.compositeVisor(ctx, color, visor, structure, view);
    if (composite) return { base: composite, overlay: '', filter: '' };
    const special = api.capabilities.overlayFor(ctx, color, visor, structure, view);
    if (special?.url) return { base: baseUrl, overlay: special.url, filter: special.filter };
    const visorOverlay = resolver.exactVisorOverlay(ctx, color, visor, structure, view);
    if (visorOverlay) return { base: baseUrl, overlay: visorOverlay, filter: '' };
    return null;
  };

  resolver.imageCandidates = (ctx, payloadFn = null) => {
    const n = api.normalize;
    const getPayload = payloadFn || ((color, visor, structure, view) => resolver.payloadFor(ctx, color, visor, structure, view));
    if (!ctx.hasFullConfig) {
      return n.unique([
        getPayload(ctx.state.color, ctx.state.visor, ctx.state.structure, ctx.state.view),
        ctx.productMedia[0]?.image ? { base: ctx.productMedia[0].image, overlay: '', filter: '' } : null
      ].filter(Boolean));
    }
    const closed = resolver.closedStructure(ctx);
    const threeQuarter = resolver.threeQuarterView(ctx);
    const clearVisor = n.itemKey(
      ctx.lists.visors.find((visor) => resolver.isClearVisor(ctx, n.itemKey(visor))) || ctx.lists.visors[0]
    );
    const combinations = n.unique([
      { color: ctx.state.color, visor: ctx.state.visor, structure: ctx.state.structure, view: ctx.state.view },
      { color: ctx.state.color, visor: ctx.state.visor, structure: closed, view: ctx.state.view },
      { color: ctx.state.color, visor: ctx.state.visor, structure: closed, view: threeQuarter },
      { color: ctx.state.color, visor: clearVisor, structure: ctx.state.structure, view: ctx.state.view },
      { color: ctx.state.color, visor: clearVisor, structure: closed, view: threeQuarter }
    ]);
    const candidates = combinations
      .map((combination) => getPayload(combination.color, combination.visor, combination.structure, combination.view))
      .filter(Boolean);
    return n.unique(candidates);
  };

  api.resolver = resolver;
})(typeof window !== 'undefined' ? window : globalThis);
